import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { code: "", type: "percent", value: 0, expiry: "", min_order: 0, usage_limit: 0, active: true };

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data.coupons));
    useEffect(() => { load(); }, []);

    const save = async () => {
        const payload = { ...editing, value: Number(editing.value), min_order: Number(editing.min_order), usage_limit: Number(editing.usage_limit), expiry: editing.expiry || null };
        try {
            if (editing.id) await api.put(`/admin/coupons/${editing.id}`, payload);
            else await api.post("/admin/coupons", payload);
            toast.success("Coupon saved"); setEditing(null); load();
        } catch (err) { toast.error(apiError(err)); }
    };
    const remove = async (id) => { if (!window.confirm("Delete coupon?")) return; await api.delete(`/admin/coupons/${id}`); load(); toast.success("Deleted"); };

    const inp = "w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-gold";
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold">Coupons</h1>
                <button data-testid="add-coupon" onClick={() => setEditing({ ...EMPTY })} className="bg-ink text-white px-4 py-2.5 rounded text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add Coupon</button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase"><tr><th className="text-left p-4">Code</th><th className="text-left p-4">Discount</th><th className="text-left p-4">Min Order</th><th className="text-left p-4">Used</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                        {coupons.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">No coupons.</td></tr> :
                        coupons.map((c) => (
                            <tr key={c.id}>
                                <td className="p-4 font-medium">{c.code}</td>
                                <td className="p-4">{c.type === "percent" ? `${c.value}%` : `₹${c.value}`}</td>
                                <td className="p-4">₹{c.min_order || 0}</td>
                                <td className="p-4">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                                <td className="p-4"><span className={`text-xs px-2 py-1 rounded ${c.active ? "bg-green-100 text-green-700" : "bg-gray-100"}`}>{c.active ? "Active" : "Off"}</span></td>
                                <td className="p-4 text-right"><button onClick={() => setEditing({ ...c, expiry: c.expiry ? c.expiry.slice(0, 10) : "" })} className="p-2 hover:bg-gray-100 rounded"><Pencil className="h-4 w-4" /></button><button onClick={() => remove(c.id)} className="p-2 hover:bg-gray-100 rounded text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} Coupon</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <input data-testid="coupon-code" placeholder="CODE" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} className={inp} />
                            <div className="grid grid-cols-2 gap-3">
                                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className={inp}><option value="percent">Percentage</option><option value="fixed">Fixed ₹</option></select>
                                <input type="number" placeholder="Value" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className={inp} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" placeholder="Min order" value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} className={inp} />
                                <input type="number" placeholder="Usage limit (0=∞)" value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} className={inp} />
                            </div>
                            <div><label className="text-xs text-gray-500">Expiry (optional)</label><input type="date" value={editing.expiry} onChange={(e) => setEditing({ ...editing, expiry: e.target.value })} className={inp} /></div>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-gold" /> Active</label>
                            <button data-testid="coupon-save" onClick={save} className="w-full bg-ink text-white py-2.5 rounded text-sm">Save Coupon</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
